/**
 * OrdersDefaultClientSection — which client the Orders page opens on at the
 * selected location (BLU-1423).
 *
 * Saves on pick, per location, with a toast (ShippingProviderSettingsSection's
 * pattern). The choices are the owner's own org plus the clients with an
 * ACTIVE junction here, read from the same `listConnectedClientsAtLocation`
 * cache the Orders page switcher uses, so the two never offer different lists.
 * Only the org that runs the location can change it; anyone else sees it
 * read-only. A saved client that has since disconnected is called out, with a
 * Clear action (the picker already reads All clients, so choosing it fires no
 * change), since the Orders page then opens on All clients.
 *
 * The saved value rides `locations.list` (owned rows only), the same row the
 * Orders page reads, so this card makes no Settings read of its own.
 */
import { useTranslation } from 'react-i18next';
import { Info, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { NoteLine } from '@/components/shared/NoteLine';
import { SectionError } from '@/components/shared/SectionError';
import { useLocation } from '@/contexts/LocationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { trpc } from '@/lib/trpc';
import { ALL_CLIENTS } from '@/features/inventory/hooks/useClientOrgFilter';
import { useConnectedClients } from '@/features/inventory/hooks/useConnectedClients';

interface OrdersDefaultClientSectionProps {
  locationId: string;
}

export function OrdersDefaultClientSection({ locationId }: OrdersDefaultClientSectionProps) {
  const { t } = useTranslation('settings');
  const { organization } = useOrganization();
  const { allLocations } = useLocation();
  const utils = trpc.useUtils();
  const sharedLocationsEnabled =
    (import.meta.env.VITE_SHARED_LOCATIONS_ENABLED ?? '') === 'true';

  // The default is only ever set on rows the caller's org owns (the server
  // nulls it everywhere else), so anything that is not an owned row is not
  // the caller's to edit.
  const found = allLocations.find((l) => l.id === locationId);
  const location =
    found && found.is_shared !== true && found.owner_organization_id === organization?.id ? found : null;

  const {
    clients,
    isLoading: clientsLoading,
    isError: clientsFailed,
  } = useConnectedClients({ locationId, enabled: sharedLocationsEnabled && location !== null });

  const allClientsLabel = t('crossOrg.filter.allClients', { ns: 'inventory' });
  const ownOrg = organization ? { id: organization.id, name: organization.name } : null;
  const otherClients = clients.filter((c) => c.id !== ownOrg?.id);

  const nameFor = (orgId: string | null): string => {
    if (orgId === null) return allClientsLabel;
    if (orgId === ownOrg?.id) return ownOrg.name;
    return clients.find((c) => c.id === orgId)?.name ?? orgId;
  };

  const saveMutation = trpc.productionSettings.setLocationQueueDefaultClient.useMutation({
    onSuccess: (_data, variables) => {
      toast.success(t('production.ordersDefaultClient.saved', { name: nameFor(variables.clientOrgId) }));
      // locations.list carries the default to the Orders page and to this card.
      void utils.locations.list.invalidate();
    },
    onError: (error) => {
      toast.error(t('production.ordersDefaultClient.failed'), { description: error.message });
    },
  });

  if (!location) {
    return <NoteLine icon={Info}>{t('production.ordersDefaultClient.readOnly')}</NoteLine>;
  }

  if (clientsFailed) {
    return (
      <SectionError
        variant="compact"
        body={t('production.ordersDefaultClient.loadError')}
        onRetry={() => void utils.locations.listConnectedClientsAtLocation.invalidate({ locationId })}
        retryLabel={t('retry', { ns: 'common' })}
      />
    );
  }

  if (clientsLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status" aria-busy="true">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        {t('loading', { ns: 'common' })}
      </div>
    );
  }

  const savedId = location.queue_default_client_org_id ?? null;
  const savedIsLive = savedId !== null && (savedId === ownOrg?.id || otherClients.some((c) => c.id === savedId));
  const isStale = savedId !== null && !savedIsLive;
  const isPending = saveMutation.isPending;

  // Checked before the no-clients case: a saved client that has since left
  // the location is the one state that still needs an action.
  if (otherClients.length === 0 && !isStale) {
    return <NoteLine icon={Info}>{t('production.ordersDefaultClient.noClients')}</NoteLine>;
  }

  return (
    <div className="space-y-2">
      {otherClients.length > 0 ? (
        <div className="flex items-center gap-2">
          <Select
            value={savedIsLive ? savedId : ALL_CLIENTS}
            onValueChange={(next) =>
              saveMutation.mutate({ locationId, clientOrgId: next === ALL_CLIENTS ? null : next })
            }
            disabled={isPending}
          >
            <SelectTrigger className="h-9 w-[260px] text-sm" aria-label={t('production.ordersDefaultClient.title')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_CLIENTS}>{allClientsLabel}</SelectItem>
              {ownOrg ? (
                <SelectItem value={ownOrg.id}>
                  {t('crossOrg.picker.youSuffix', { ns: 'inventory', name: ownOrg.name })}
                </SelectItem>
              ) : null}
              {otherClients.map((client) => (
                <SelectItem key={client.id} value={client.id}>
                  {client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isPending ? (
            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" aria-hidden="true" />
          ) : null}
        </div>
      ) : null}
      {isStale ? (
        <NoteLine icon={AlertTriangle} tone="warning">
          {t('production.ordersDefaultClient.stale')}{' '}
          <Button
            variant="link"
            className="h-auto p-0 align-baseline text-xs font-medium"
            disabled={isPending}
            onClick={() => saveMutation.mutate({ locationId, clientOrgId: null })}
          >
            {t('production.ordersDefaultClient.clear')}
          </Button>
        </NoteLine>
      ) : null}
    </div>
  );
}
